import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import BackButton from "@/components/BackButton";
import PilotCompare from "@/components/PilotCompare";
import StateControl from "@/components/StateControl";
import EnrollmentSection from "@/components/EnrollmentSection";
import PilotMonthBadge from "@/components/PilotMonthBadge";
import { StateBadge } from "@/lib/states";
import { Trophy, Calendar, MapPin, ChevronRight, Medal, Radio, Cpu, Pencil, X, Save, FileText, Share2, Timer, Trash2, RotateCcw } from "lucide-react";

const CAT_IMG = {
  rc: "https://images.unsplash.com/photo-1613404198873-14a30017543a?crop=entropy&cs=srgb&fm=jpg&q=85",
  sim: "https://images.unsplash.com/photo-1743649978995-c76212449e15?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const medalColor = (rank) =>
  rank === 1 ? "text-yellow-400" : rank === 2 ? "text-zinc-300" : rank === 3 ? "text-amber-600" : "text-zinc-500";

const SESSION_LABEL = {
  gara: "Gara",
  prove_libere: "Prove Libere",
  qualifica: "Qualifica",
};

export default function ChampionshipDetail() {
  const { id } = useParams();
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [champ, setChamp] = useState(null);
  const [champError, setChampError] = useState(false);
  const [events, setEvents] = useState([]);
  const [standings, setStandings] = useState([]);
  const [records, setRecords] = useState([]);
  const [tab, setTab] = useState("standings");
  const [regOpen, setRegOpen] = useState(false);
  const [regText, setRegText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editPilot, setEditPilot] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPoints, setEditPoints] = useState("");
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState([]);
  const { tick } = useActivity();

  useEffect(() => {
    setChampError(false);
    api.get(`/championships/${id}`).then((r) => setChamp(r.data)).catch(() => setChampError(true));
    api.get(`/championships/${id}/events`).then((r) => setEvents(r.data)).catch(() => {});
    api.get(`/championships/${id}/standings`).then((r) => setStandings(r.data)).catch(() => {});
    api.get(`/championships/${id}/records`).then((r) => setRecords(r.data)).catch(() => {});
  }, [id, tick]);

  const saveRegulation = async () => {
    setSaving(true);
    try {
      const payload = { ...champ, regulation: regText };
      delete payload.id;
      const { data } = await api.put(`/championships/${id}`, payload);
      setChamp(data);
      setRegOpen(false);
      toast.success("Regolamento aggiornato");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const reloadStandings = () => api.get(`/championships/${id}/standings`).then((r) => setStandings(r.data)).catch(() => {});

  const loadTrash = () =>
    api.get(`/championships/${id}/pilot-trash`).then((r) => setTrash(r.data)).catch(() => {});

  const openTrash = () => { loadTrash(); setTrashOpen(true); };

  const openEditPilot = (row) => {
    setEditPilot(row);
    setEditName(row.name);
    setEditPoints(String(row.points));
  };

  const savePilotEdit = async () => {
    if (!editPilot) return;
    setSaving(true);
    try {
      await api.put(`/championships/${id}/pilots/${encodeURIComponent(editPilot.name)}`, {
        new_name: editName.trim(),
        points: Number(editPoints),
      });
      setEditPilot(null);
      await reloadStandings();
      toast.success("Pilota aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const deletePilot = async (row, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Eliminare "${row.name}" dal campionato? Potrai ripristinarlo dal Cestino piloti.`)) return;
    try {
      await api.delete(`/championships/${id}/pilots/${encodeURIComponent(row.name)}`);
      await reloadStandings();
      toast.success(`${row.name} eliminato. Ripristinabile dal Cestino piloti.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const restorePilot = async (t) => {
    try {
      await api.post(`/championships/${id}/pilot-trash/${t.id}/restore`);
      await Promise.all([reloadStandings(), loadTrash()]);
      toast.success(`${t.name} ripristinato`);
    } catch (err) {
      toast.error("Errore nel ripristino");
    }
  };

  const purgePilot = async (t) => {
    if (!window.confirm(`Eliminare definitivamente "${t.name}"? Questa azione non è reversibile.`)) return;
    try {
      await api.delete(`/championships/${id}/pilot-trash/${t.id}`);
      await loadTrash();
      toast.success("Rimosso definitivamente");
    } catch (err) {
      toast.error("Errore");
    }
  };


  if (champError) return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
      <BackButton className="mb-6" />
      <p className="text-zinc-400" data-testid="champ-error">Impossibile caricare il campionato. Potrebbe non essere più disponibile.</p>
    </div>
  );
  if (!champ) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Caricamento…</div>;

  const canManage = isAdmin || !!(user && user.id && champ.owner_id && champ.owner_id === user.id);
  const reloadChamp = () => api.get(`/championships/${id}`).then((r) => setChamp(r.data)).catch(() => {});

  const shareStandings = async () => {
    const accent = champ.category === "rc" ? "#facc15" : "#06b6d4";
    const W = 1080, rowH = 66, headH = 260;
    const H = headH + standings.length * rowH + 50;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#09090b"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = accent; ctx.fillRect(0, 0, W, 10);
    const logo = new Image();
    logo.src = "/team-tuono-logo.png";
    await new Promise((res) => { logo.onload = res; logo.onerror = res; });
    if (logo.complete && logo.naturalWidth) {
      try { ctx.drawImage(logo, W - 150, 38, 104, 104); } catch (e) { /* skip */ }
    }
    ctx.fillStyle = accent; ctx.font = "bold 26px Arial"; ctx.textAlign = "left";
    ctx.fillText("TEAM TUONO", 48, 82);
    ctx.fillStyle = "#fafafa"; ctx.font = "900 52px Arial";
    ctx.fillText(champ.name.toUpperCase().slice(0, 22), 48, 150);
    ctx.fillStyle = "#a1a1aa"; ctx.font = "24px Arial";
    ctx.fillText(champ.category === "rc" ? "Classifica · Corse RC" : `Classifica · ${champ.platform || "Simulatore"}`, 48, 200);
    let y = headH;
    standings.forEach((r, i) => {
      ctx.fillStyle = i % 2 ? "#0e0e11" : "#16161a";
      ctx.fillRect(48, y - 42, W - 96, rowH - 8);
      ctx.fillStyle = r.rank === 1 ? "#facc15" : r.rank === 2 ? "#d4d4d8" : r.rank === 3 ? "#d97706" : "#71717a";
      ctx.font = "bold 32px Arial"; ctx.textAlign = "left";
      ctx.fillText(String(r.rank), 74, y);
      ctx.fillStyle = "#fafafa"; ctx.font = "bold 30px Arial";
      ctx.fillText(r.name.slice(0, 24), 160, y);
      ctx.fillStyle = accent; ctx.font = "900 34px Arial"; ctx.textAlign = "right";
      ctx.fillText(String(r.points) + " pt", W - 74, y);
      y += rowH;
    });
    ctx.textAlign = "left";
    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    const file = new File([blob], "classifica.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: champ.name, text: `Classifica ${champ.name} — Team Tuono` });
        return;
      }
    } catch (e) { /* fallback to download */ }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `classifica-${champ.name}.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Locandina classifica generata");
  };

  return (
    <div>
      <div className="relative h-64 overflow-hidden grain">
        <img src={champ.image_url || CAT_IMG[champ.category]} alt={champ.name}
          className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-8">
          <span className="flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] uppercase text-cyan-400">
            {champ.category === "rc" ? <Radio className="h-3.5 w-3.5" /> : <Cpu className="h-3.5 w-3.5" />}
            {champ.category === "rc" ? "Corse RC" : `Simulatore · ${champ.platform || "SIM"}`}
          </span>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase text-zinc-50">
            {champ.name}
          </h1>
          {champ.description && <p className="mt-2 text-zinc-300 max-w-2xl">{champ.description}</p>}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <BackButton className="mb-6" />
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {champ.state && <StateBadge state={champ.state} testid="champ-state-badge" />}
          {champ.cost > 0 && (
            <span className="inline-flex items-center gap-1.5 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-xs font-bold px-3 py-1.5 rounded-full" data-testid="champ-cost-badge">
              Quota iscrizione €{champ.cost}
            </span>
          )}
        </div>
        {canManage && (
          <div className="mb-6">
            <StateControl kind="championships" id={id} state={champ.state} enrollOverride={champ.enroll_override} isAdmin={isAdmin} onChanged={reloadChamp} />
          </div>
        )}
        <div className="flex gap-2 border-b border-zinc-800 mb-8 overflow-x-auto no-scrollbar">
          {[
            { k: "standings", l: "Classifica" },
            { k: "enrolled", l: "Iscritti" },
            { k: "drivers", l: "Piloti" },
            { k: "compare", l: "Confronto" },
            { k: "records", l: "Albo Record" },
            { k: "events", l: `Gran Premi (${events.length})` },
            { k: "regulation", l: "Regolamento" },
          ].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-200 border-b-2 -mb-px whitespace-nowrap shrink-0 ${
                tab === t.k ? "border-yellow-400 text-yellow-400" : "border-transparent text-zinc-400 hover:text-zinc-100"
              }`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "standings" && (
          standings.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center" data-testid="empty-standings">
              Nessun risultato ancora. La classifica apparirà dopo il primo Gran Premio.
            </p>
          ) : (
            <>
            <div className="flex justify-end gap-2 mb-4">
              {isAdmin && (
                <button onClick={openTrash} data-testid="pilot-trash-btn"
                  className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs font-bold tracking-[0.15em] uppercase px-4 py-2.5 transition-colors duration-200">
                  <Trash2 className="h-4 w-4" /> Cestino piloti
                </button>
              )}
              <button onClick={shareStandings} data-testid="share-standings-btn"
                className="inline-flex items-center gap-2 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 text-xs font-bold tracking-[0.15em] uppercase px-4 py-2.5 transition-colors duration-200">
                <Share2 className="h-4 w-4" /> Condividi classifica
              </button>
            </div>
            <div className="border border-zinc-800" data-testid="standings-table">
              <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-zinc-900 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                <div className="col-span-2 sm:col-span-1">Pos</div>
                <div className="col-span-6 sm:col-span-6">Pilota</div>
                <div className="col-span-2 text-center hidden sm:block">Gare</div>
                <div className="col-span-2 text-center hidden sm:block">Vitt.</div>
                <div className="col-span-4 sm:col-span-1 text-right">Punti</div>
              </div>
              {standings.map((row, i) => (
                <motion.div
                  key={row.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => navigate(`/campionati/${id}/pilota/${encodeURIComponent(row.name)}`)}
                  data-testid={`standings-row-${row.rank}`}
                  className={`grid grid-cols-12 gap-2 px-5 py-4 items-center border-t border-zinc-800 cursor-pointer hover:bg-yellow-400/5 transition-colors duration-200 ${
                    i % 2 ? "bg-transparent" : "bg-zinc-900/50"
                  }`}
                >
                  <div className={`col-span-2 sm:col-span-1 font-display font-bold text-lg ${medalColor(row.rank)}`}>
                    {row.rank <= 3 ? <Medal className={`h-5 w-5 ${medalColor(row.rank)}`} /> : row.rank}
                  </div>
                  <div className="col-span-6 sm:col-span-6 font-heading font-bold text-zinc-50 hover:text-yellow-400 transition-colors duration-200 flex items-center gap-2 flex-wrap">
                    {row.name}<PilotMonthBadge name={row.name} />
                    {isAdmin && (
                      <span className="flex items-center gap-1 ml-1">
                        <button data-testid={`edit-pilot-${row.rank}`} onClick={(e) => { e.stopPropagation(); openEditPilot(row); }}
                          className="p-1 text-zinc-500 hover:text-cyan-400 transition-colors" title="Modifica pilota/punti">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`delete-pilot-${row.rank}`} onClick={(e) => deletePilot(row, e)}
                          className="p-1 text-zinc-500 hover:text-red-400 transition-colors" title="Elimina pilota">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-center text-zinc-400 hidden sm:block">{row.races}</div>
                  <div className="col-span-2 text-center text-zinc-400 hidden sm:block">{row.wins}</div>
                  <div className="col-span-4 sm:col-span-1 text-right font-display font-bold text-xl text-yellow-400">
                    {row.points}{row.points_override && <span className="text-[10px] text-cyan-400 ml-1" title="Punteggio modificato dall'admin">*</span>}
                  </div>
                </motion.div>
              ))}
            </div>
            </>
          )
        )}

        {tab === "drivers" && (
          standings.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center" data-testid="empty-drivers">Nessun pilota ancora.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="drivers-cards">
              {standings.map((d) => (
                <Link key={d.name} to={`/campionati/${id}/pilota/${encodeURIComponent(d.name)}`} data-testid={`driver-card-${d.rank}`}
                  className="relative border border-zinc-800 bg-zinc-900 p-5 block hover:border-yellow-400/50 transition-colors duration-200">
                  {isAdmin && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
                      <button data-testid={`edit-driver-${d.rank}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditPilot(d); }}
                        className="p-1.5 bg-zinc-950/70 rounded text-zinc-400 hover:text-cyan-400 transition-colors" title="Modifica">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`delete-driver-${d.rank}`} onClick={(e) => { e.preventDefault(); deletePilot(d, e); }}
                        className="p-1.5 bg-zinc-950/70 rounded text-zinc-400 hover:text-red-400 transition-colors" title="Elimina">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className={`font-display font-bold text-2xl ${medalColor(d.rank)}`}>#{d.rank}</span>
                    <span className="font-display text-2xl text-yellow-400">{d.points}<span className="text-xs text-zinc-500 ml-1">pt</span></span>
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-bold text-zinc-50 truncate">{d.name}</h3>
                  <div className="mt-1"><PilotMonthBadge name={d.name} /></div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-display text-zinc-100">{d.races}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Gare</div>
                    </div>
                    <div>
                      <div className="text-lg font-display text-cyan-400">{d.wins}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Vittorie</div>
                    </div>
                    <div>
                      <div className="text-lg font-display text-amber-500">{d.podiums}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Podi</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}

        {tab === "enrolled" && (
          <EnrollmentSection kind="championships" id={id} state={champ.state} enrollOverride={champ.enroll_override} />
        )}

        {tab === "compare" && (
          standings.length < 2 ? (
            <p className="text-zinc-500 py-10 text-center" data-testid="compare-need-more">
              Servono almeno due piloti in classifica per il confronto.
            </p>
          ) : (
            <PilotCompare champId={id} drivers={standings.map((s) => s.name)} />
          )
        )}

        {tab === "records" && (
          records.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center" data-testid="empty-records">
              Nessun record ancora. Servono tempi giro nei risultati dei Gran Premi.
            </p>
          ) : (
            <div className="border border-zinc-800" data-testid="records-table">
              <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-zinc-900 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                <div className="col-span-5">Circuito</div>
                <div className="col-span-4">Pilota</div>
                <div className="col-span-3 text-right">Record</div>
              </div>
              {records.map((rec, i) => (
                <motion.div key={rec.location + i}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                  data-testid={`record-row-${i}`}
                  className={`grid grid-cols-12 gap-2 px-5 py-4 items-center border-t border-zinc-800 ${i % 2 ? "bg-transparent" : "bg-zinc-900/50"}`}>
                  <div className="col-span-5 flex items-center gap-2 font-heading font-bold text-zinc-50">
                    <MapPin className="h-4 w-4 text-fuchsia-300 shrink-0" /> {rec.location}
                  </div>
                  <div className="col-span-4">
                    <button onClick={() => navigate(`/campionati/${id}/pilota/${encodeURIComponent(rec.name)}`)}
                      className="font-heading font-bold text-zinc-100 hover:text-yellow-400 transition-colors duration-200 text-left">
                      {rec.name}
                    </button>
                    {rec.event_name && <div className="text-[10px] uppercase tracking-wider text-zinc-500">{rec.event_name}</div>}
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-1.5 font-display font-bold text-lg text-fuchsia-300 tabular-nums">
                    <Timer className="h-4 w-4" /> {rec.time}
                  </div>
                </motion.div>
              ))}
            </div>
          )
        )}



        {tab === "events" && (
          events.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center" data-testid="empty-events">Nessun Gran Premio programmato.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((e) => (
                <Link key={e.id} to={`/eventi/${e.id}`} data-testid={`event-card-${e.id}`}
                  className="group border border-zinc-800 bg-zinc-900 p-6 hover:border-cyan-400/50 transition-colors duration-200">
                  <Trophy className="h-6 w-6 text-cyan-400 mb-3" />
                  <span className="inline-block text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500 mb-1">
                    {SESSION_LABEL[e.session_type] || "Gara"}
                  </span>
                  <h3 className="font-heading text-lg font-bold text-zinc-50">{e.name}</h3>
                  <div className="mt-3 space-y-1 text-sm text-zinc-400">
                    {e.date && <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" />{e.date}</div>}
                    {e.location && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{e.location}</div>}
                  </div>
                  <div className="mt-4 flex items-center gap-1 text-xs font-bold tracking-[0.15em] uppercase text-cyan-400 group-hover:gap-2 transition-all duration-200">
                    Risultati <ChevronRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          )
        )}

        {tab === "regulation" && (
          <div data-testid="regulation-content">
            <div className="flex items-center justify-between mb-4">
              <span className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">
                <FileText className="h-4 w-4" /> Regolamento
              </span>
              {isAdmin && (
                <button
                  data-testid="edit-regulation-btn"
                  onClick={() => { setRegText(champ.regulation || ""); setRegOpen(true); }}
                  className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
                >
                  <Pencil className="h-3.5 w-3.5" /> Modifica
                </button>
              )}
            </div>
            <div className="border border-zinc-800 bg-zinc-900 p-8">
              {champ.regulation ? (
                <p className="whitespace-pre-wrap text-zinc-300 text-base leading-relaxed">{champ.regulation}</p>
              ) : (
                <p className="text-zinc-500">Nessun regolamento pubblicato.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {regOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto border border-zinc-800 bg-zinc-900 p-6" data-testid="regulation-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold uppercase text-zinc-50">Modifica Regolamento</h3>
              <button onClick={() => setRegOpen(false)} className="text-zinc-400 hover:text-zinc-100 transition-colors duration-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <textarea
              data-testid="regulation-textarea"
              value={regText}
              onChange={(e) => setRegText(e.target.value)}
              rows={14}
              placeholder="Scrivi qui il regolamento del campionato…"
              className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none p-4 text-zinc-100 text-sm leading-relaxed transition-colors duration-200"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setRegOpen(false)}
                className="px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors duration-200">
                Annulla
              </button>
              <button onClick={saveRegulation} disabled={saving} data-testid="save-regulation-btn"
                className="inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3 hover:bg-yellow-300 transition-colors duration-200 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
      {editPilot && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-md my-8 border border-zinc-800 bg-zinc-900 p-6" data-testid="edit-pilot-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold uppercase text-zinc-50">Modifica pilota</h3>
              <button onClick={() => setEditPilot(null)} className="text-zinc-400 hover:text-zinc-100 transition-colors duration-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1.5">Nome pilota</label>
            <input data-testid="edit-pilot-name" value={editName} onChange={(e) => setEditName(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none p-3 text-zinc-100 text-sm mb-4 transition-colors duration-200" />
            <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1.5">Punti in classifica</label>
            <input data-testid="edit-pilot-points" type="number" value={editPoints} onChange={(e) => setEditPoints(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none p-3 text-zinc-100 text-sm transition-colors duration-200" />
            <p className="mt-2 text-xs text-zinc-500">Modificare i punti imposta un valore manuale in classifica.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditPilot(null)}
                className="px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors duration-200">
                Annulla
              </button>
              <button onClick={savePilotEdit} disabled={saving} data-testid="save-pilot-btn"
                className="inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3 hover:bg-yellow-300 transition-colors duration-200 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {trashOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto border border-zinc-800 bg-zinc-900 p-6" data-testid="pilot-trash-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold uppercase text-zinc-50 flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-400" /> Cestino piloti
              </h3>
              <button onClick={() => setTrashOpen(false)} className="text-zinc-400 hover:text-zinc-100 transition-colors duration-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            {trash.length === 0 ? (
              <p className="text-zinc-500 py-8 text-center" data-testid="empty-trash">Nessun pilota eliminato.</p>
            ) : (
              <div className="space-y-2">
                {trash.map((t) => (
                  <div key={t.id} data-testid={`trash-row-${t.id}`}
                    className="flex items-center justify-between border border-zinc-800 bg-zinc-950 px-4 py-3">
                    <div>
                      <div className="font-heading font-bold text-zinc-100">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.results_count} risultati archiviati</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => restorePilot(t)} data-testid={`restore-${t.id}`}
                        className="inline-flex items-center gap-1.5 border border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs font-bold uppercase px-3 py-2 transition-colors duration-200">
                        <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                      </button>
                      <button onClick={() => purgePilot(t)} data-testid={`purge-${t.id}`}
                        className="p-2 text-zinc-500 hover:text-red-400 transition-colors" title="Elimina definitivamente">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
